<template>
  <Modal
    :show="show"
    :title="modalTitle"
    size="large"
    @close="handleClose"
  >
    <div class="space-y-4">
      <div class="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40">
        <div class="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            :disabled="!cropperReady"
            @click="setAspectMode('recommended')"
            :class="aspectMode === 'recommended' ? activeToolButtonClass : toolButtonClass"
          >
            推荐方形
          </button>
          <button
            type="button"
            :disabled="!cropperReady"
            @click="setAspectMode('free')"
            :class="aspectMode === 'free' ? activeToolButtonClass : toolButtonClass"
          >
            自由裁剪
          </button>
          <span class="text-xs text-gray-500 dark:text-gray-400">
            推荐比例约 {{ RECOMMENDED_ASPECT_RATIO_LABEL }}
          </span>
        </div>

        <div class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-950">
          <div class="relative h-[520px] w-full overflow-hidden bg-grid-pattern">
            <img
              ref="imageRef"
              :src="sourceImageUrl"
              alt="待裁剪 Logo"
              class="block max-h-none max-w-none"
            />
          </div>
        </div>

        <p class="mt-3 text-xs text-gray-500 dark:text-gray-400">
          说明：当前推荐上传方形品牌图标，请以主站导航栏中的品牌图标展示效果为最终参考。
        </p>
      </div>

      <div class="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900/40">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_auto]">
          <div>
            <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">缩放</p>
            <div class="flex flex-wrap items-center gap-2">
              <button type="button" :class="toolButtonClass" :disabled="!cropperReady" @click="zoomImage(0.1)">放大</button>
              <button type="button" :class="toolButtonClass" :disabled="!cropperReady" @click="zoomImage(-0.1)">缩小</button>
            </div>
          </div>

          <div>
            <p class="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">变换</p>
            <div class="flex flex-wrap items-center gap-2">
              <button type="button" :class="toolButtonClass" :disabled="!cropperReady" @click="rotateImage(-90)">左转</button>
              <button type="button" :class="toolButtonClass" :disabled="!cropperReady" @click="rotateImage(90)">右转</button>
            </div>
          </div>

          <div class="flex items-end lg:justify-end">
            <button type="button" :class="toolButtonClass" :disabled="!cropperReady" @click="resetCrop">重置</button>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6 flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
      <button
        type="button"
        :disabled="confirming"
        class="inline-flex items-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
        @click="handleClose"
      >
        取消
      </button>
      <button
        type="button"
        :disabled="confirming || !cropperReady"
        class="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
        @click="handleConfirm"
      >
        {{ confirming ? '上传中...' : cropperReady ? '确认并上传' : '正在初始化...' }}
      </button>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

import Modal from '@/components/common/Modal.vue'
import type { TenantLogoVariant } from '@/types/admin/tenant'

type AspectMode = 'recommended' | 'free'

const RECOMMENDED_ASPECT_RATIO = 1
const RECOMMENDED_ASPECT_RATIO_LABEL = '1:1'

interface Props {
  show: boolean
  sourceImageUrl: string
  originalFileName: string
  originalMimeType: string
  variant: TenantLogoVariant
  confirming?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  confirming: false,
})

const emit = defineEmits<{
  close: []
  confirm: [file: File]
}>()

const imageRef = ref<HTMLImageElement | null>(null)
const cropperRef = ref<Cropper | null>(null)
const aspectMode = ref<AspectMode>('recommended')
const cropperReady = ref(false)

const toolButtonClass = computed(() =>
  cropperReady.value
    ? 'inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800'
    : 'inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 opacity-50 cursor-not-allowed dark:border-gray-600 dark:text-gray-200'
)
const activeToolButtonClass = computed(() =>
  cropperReady.value
    ? 'inline-flex items-center rounded-lg border border-indigo-500 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-100 dark:border-indigo-400 dark:bg-indigo-900/40 dark:text-indigo-200 dark:hover:bg-indigo-900/60'
    : 'inline-flex items-center rounded-lg border border-indigo-500 bg-indigo-50 px-3 py-2 text-sm font-medium text-indigo-700 opacity-50 cursor-not-allowed dark:border-indigo-400 dark:bg-indigo-900/40 dark:text-indigo-200'
)

/**
 * 弹窗标题。
 */
const modalTitle = computed(() => (props.variant === 'light' ? '编辑浅色 Logo' : '编辑深色 Logo'))

/**
 * 解析导出图片的 MIME 类型。
 */
const exportMimeType = computed(() => {
  const normalized = props.originalMimeType.toLowerCase()
  if (['image/png', 'image/jpeg', 'image/webp'].includes(normalized)) {
    return normalized
  }
  return 'image/png'
})

/**
 * 解析导出图片扩展名。
 */
const exportExtension = computed(() => {
  if (exportMimeType.value === 'image/jpeg') {
    return 'jpg'
  }
  if (exportMimeType.value === 'image/webp') {
    return 'webp'
  }
  return 'png'
})

/**
 * 销毁现有 Cropper 实例。
 */
const destroyCropper = () => {
  cropperRef.value?.destroy()
  cropperRef.value = null
  cropperReady.value = false
}

/**
 * 初始化 Cropper。
 */
const initCropper = async () => {
  if (!props.show || !props.sourceImageUrl) {
    return
  }

  await nextTick()

  if (!imageRef.value) {
    return
  }

  destroyCropper()
  aspectMode.value = 'recommended'

  const img = imageRef.value
  if (!img.complete) {
    await new Promise<void>((resolve) => {
      img.onload = () => resolve()
      img.onerror = () => resolve()
    })
  }

  cropperRef.value = new Cropper(imageRef.value, {
    aspectRatio: RECOMMENDED_ASPECT_RATIO,
    viewMode: 1,
    dragMode: 'move',
    autoCropArea: 1,
    background: false,
    responsive: true,
    checkOrientation: false,
    guides: true,
    movable: true,
    zoomable: true,
    scalable: false,
    rotatable: true,
    ready: () => {
      cropperReady.value = true
    },
  })
}

/**
 * 设置裁剪比例模式。
 */
const setAspectMode = (mode: AspectMode) => {
  if (!cropperReady.value) return
  aspectMode.value = mode
  cropperRef.value?.setAspectRatio(mode === 'recommended' ? RECOMMENDED_ASPECT_RATIO : NaN)
}

/**
 * 缩放图片。
 */
const zoomImage = (delta: number) => {
  if (!cropperReady.value) return
  cropperRef.value?.zoom(delta)
}

/**
 * 旋转图片。
 */
const rotateImage = (degree: number) => {
  if (!cropperReady.value) return
  cropperRef.value?.rotate(degree)
}

/**
 * 重置裁剪状态。
 */
const resetCrop = () => {
  if (!cropperReady.value || !cropperRef.value) return
  cropperRef.value.reset()
  cropperRef.value.setAspectRatio(
    aspectMode.value === 'recommended' ? RECOMMENDED_ASPECT_RATIO : NaN,
  )
}

/**
 * 关闭弹窗。
 */
const handleClose = () => {
  if (props.confirming) {
    return
  }
  emit('close')
}

/**
 * 生成裁剪后的文件并确认上传。
 */
const handleConfirm = async () => {
  if (!cropperRef.value) {
    return
  }

  const canvas = cropperRef.value.getCroppedCanvas({
    maxWidth: 1880,
    maxHeight: 1880,
    imageSmoothingEnabled: true,
    imageSmoothingQuality: 'high',
    fillColor: 'rgba(255,255,255,0)',
  })

  if (!canvas) {
    return
  }

  const quality = exportMimeType.value === 'image/png' ? undefined : 0.92
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, exportMimeType.value, quality)
  })

  if (!blob) {
    return
  }

  const baseName = props.originalFileName.includes('.')
    ? props.originalFileName.slice(0, props.originalFileName.lastIndexOf('.'))
    : props.originalFileName || `tenant-logo-${props.variant}`

  const exportFile = new File(
    [blob],
    `${baseName}-${props.variant}.${exportExtension.value}`,
    { type: exportMimeType.value },
  )

  emit('confirm', exportFile)
}

watch(
  () => [props.show, props.sourceImageUrl] as const,
  ([show, sourceImageUrl]) => {
    if (!show || !sourceImageUrl) {
      destroyCropper()
      return
    }

    void initCropper()
  },
)

onBeforeUnmount(() => {
  destroyCropper()
})
</script>

<style scoped>
.bg-grid-pattern {
  background-color: #f8fafc;
  background-image:
    linear-gradient(to right, rgba(148, 163, 184, 0.12) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(148, 163, 184, 0.12) 1px, transparent 1px);
  background-size: 20px 20px;
}

:global(.cropper-bg) {
  background-image:
    linear-gradient(45deg, rgba(148, 163, 184, 0.08) 25%, transparent 25%, transparent 75%, rgba(148, 163, 184, 0.08) 75%, rgba(148, 163, 184, 0.08)),
    linear-gradient(45deg, rgba(148, 163, 184, 0.08) 25%, transparent 25%, transparent 75%, rgba(148, 163, 184, 0.08) 75%, rgba(148, 163, 184, 0.08));
  background-position: 0 0, 10px 10px;
  background-size: 20px 20px;
}
</style>
